import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { AddressField } from "@/components/profile/AddressField";

const formatPhoneNumber = (value: string) => {
  // Remove all non-digits and any leading country code
  const numbers = value.replace(/\D/g, '').replace(/^61|^\+61/, '0');
  return numbers;
};

const validateAustralianMobile = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (!numbers.startsWith('04')) {
    return "Mobile number must start with 04";
  }
  if (numbers.length !== 10) {
    return "Mobile number must be 10 digits";
  }
  return true;
};

export function PersonalDetailsSection() {
  const form = useFormContext();
  const [showTFN, setShowTFN] = useState(false);

  return (
    <div className="space-y-6 animate-fade-up">
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Full Name Field */}
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter your name as it appears on your tax records"
                      className="bg-background"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter your name as it appears on your tax records
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      className="bg-background"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    We'll send your tax return updates to this email
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone Field */}
            <FormField
              control={form.control}
              name="phone"
              rules={{
                validate: validateAustralianMobile
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mobile Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Start with 04"
                      className="bg-background"
                      {...field}
                      value={formatPhoneNumber(field.value || '')}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        if (formatted.length <= 10) {
                          field.onChange(formatted);
                        }
                      }}
                      maxLength={10}
                    />
                  </FormControl>
                  <FormDescription>
                    Australian mobile number starting with 04
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* TFN Field */}
            <FormField
              control={form.control}
              name="tfn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax File Number (TFN)</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        type={showTFN ? "text" : "password"}
                        placeholder="Enter your 9-digit TFN"
                        className="bg-background pr-10"
                        {...field}
                        maxLength={9}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d]/g, '');
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowTFN(!showTFN)}
                    >
                      {showTFN ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <FormDescription>
                    Your TFN is securely encrypted and stored
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

          </div>

          {/* Address Field with Google Maps Autocomplete */}
          <div className="mt-6">
            <AddressField form={form} isEditing={true} />
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Your personal information is encrypted and securely stored in accordance with Australian privacy laws and ATO requirements.
        </AlertDescription>
      </Alert>
    </div>
  );
}